import { type FunctionComponent, type MouseEvent, useCallback } from 'react';
import { FormattedNumber } from 'react-intl';

import type { IPaymentMethod } from '@endeavour/payment-integration';
import { FeeType, MethodType } from '@endeavour/payment-integration';

import { isInApp } from '../../../main/util/is-app';
import { ContextLink } from '../../../shared/components/context-link/context-link';
import { FormattedMessage } from '../../../shared/components/formatted-message/formatted-message';
import { Icon } from '../../../shared/components/icon/icon';
import { useDesktop } from '../../../shared/hooks/breakpoint';
import { useCurrentCurrency } from '../../../shared/hooks/use-current-currency';
import { createStyleHelper, createClassName } from '../../../shared/utils/class-names';
import { getAssetUrl } from '../../../shared/utils/get-asset-url';

import styles from './payment-method.module.scss';

const style = createStyleHelper(styles, 'payment-method');

export interface IPaymentMethodProps {
    className?: string;
    paymentMethod: IPaymentMethod;
    onSelect?: (method: IPaymentMethod) => void;
    accountId?: string;
    savedMethodName?: string;
}

export const PaymentMethod: FunctionComponent<IPaymentMethodProps> = ({
    className,
    paymentMethod,
    accountId,
    savedMethodName,
    onSelect,
}) => {
    const isDesktop = useDesktop();
    const currency = useCurrentCurrency();
    const isNativeApp = isInApp();
    const onSelectMethod = useCallback(
        (ev: MouseEvent) => {
            if (!onSelect) {
                return;
            }

            ev.preventDefault();
            onSelect(paymentMethod);
        },
        [onSelect, paymentMethod],
    );

    const type = paymentMethod.type == MethodType.DEPOSIT ? 'deposit' : 'withdrawal';
    const location = accountId
        ? `/cashier/${type}/${paymentMethod.integrationId}/${paymentMethod.slug}/${accountId}`
        : `/cashier/${type}/${paymentMethod.integrationId}/${paymentMethod.slug}`;
    const imageUrl = getAssetUrl(paymentMethod.image);
    console.log(isNativeApp, 'IS IN APP CASHIER');

    return (
        <ContextLink
            className={createClassName([style(), className])}
            to={location}
            onClick={onSelectMethod}
        >
            <div className={style('icon-section')}>
                {imageUrl ? <img className={style('icon-image')} src={imageUrl} /> : null}
            </div>
            <div className={style('information')}>
                <div className={style('method-name')}>{paymentMethod.name}</div>
                <div className={style('range')}>
                    {paymentMethod.min ? (
                        <div className={style('range-min')}>
                            <FormattedMessage
                                id="cashier.select-method.min-amount"
                                defaultMessage="Min: {minimumAmount}"
                                values={{ minimumAmount: paymentMethod.min }}
                            />
                        </div>
                    ) : null}
                    {paymentMethod.max ? (
                        <div className={style('range-max')}>
                            <FormattedMessage
                                id="cashier.select-method.max-amount"
                                defaultMessage="Max: {maximumAmount}"
                                values={{ maximumAmount: paymentMethod.max }}
                            />
                        </div>
                    ) : null}
                </div>
                <div className={style('fee')}>
                    {paymentMethod.feeType ? (
                        <FormattedMessage
                            id="cashier.select-method.fese"
                            defaultMessage="Fee: <fee-amount></fee-amount>"
                            values={{
                                'fee-amount': () => (
                                    <FormattedNumber
                                        style={
                                            paymentMethod.feeType === FeeType.FIXED
                                                ? 'currency'
                                                : 'percent'
                                        }
                                        currency={currency}
                                        value={
                                            paymentMethod.feeType === FeeType.FIXED
                                                ? paymentMethod.feeValue ?? 0
                                                : paymentMethod.feePercentage ?? 0
                                        }
                                    />
                                ),
                            }}
                        />
                    ) : null}
                </div>
                {savedMethodName ? (
                    <div className={style('saved-name')} title={savedMethodName}>
                        {savedMethodName}
                    </div>
                ) : null}
            </div>
            {!isDesktop && (
                <div className={style('arrows-section')}>
                    <Icon className={style('arrow')} name={'angle-right'} />
                </div>
            )}
        </ContextLink>
    );
};
